import React, { useState, useEffect, useRef } from 'react';
import { companyApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import RolesPermissionsTab from '../components/RolesPermissionsTab';
import OnboardingTour from '../components/OnboardingTour';
import SettlementScheduleCard from '../components/SettlementScheduleCard';
import UserManagement from './UserManagement';
import { 
  Building2, 
  Upload, 
  Trash2, 
  Save,
  Phone,
  Mail,
  Globe,
  MapPin,
  FileText,
  Image as ImageIcon,
  Wrench,
  AlertTriangle,
  Sparkles,
  ShieldCheck,
  Sliders,
  PlayCircle,
  Users as UsersIcon,
  MessageSquare,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { contactApi } from '../lib/api';

export default function CompanySettings() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [migratingFees, setMigratingFees] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
  const [sendingContact, setSendingContact] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    street_address: '',
    city: '',
    state: '',
    zipcode: '',
    phone: '',
    email: '',
    website: '',
    mc_number: '',
    dot_number: '',
    ai_assistant_enabled: true,
    default_broker: '',
    use_default_broker: false,
    show_ytd_on_settlements: false,
    week_start_day: 6,                  // 0=Mon … 5=Sat, 6=Sun
    feature_toggles: {
      schedule: true,
      accounting: true,
      deductions: true,
      recurring_deductions: true,
      history: true,
      owner_operators: true,
      vehicle_management: true,
      driver_files: true,
    },
  });

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    try {
      const res = await companyApi.get();
      setCompany(res.data);
      setFormData({
        name: res.data.name || '',
        street_address: res.data.street_address || '',
        city: res.data.city || '',
        state: res.data.state || '',
        zipcode: res.data.zipcode || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
        mc_number: res.data.mc_number || '',
        dot_number: res.data.dot_number || '',
        ai_assistant_enabled: res.data.ai_assistant_enabled !== false,
        default_broker: res.data.default_broker || '',
        use_default_broker: !!res.data.use_default_broker,
        show_ytd_on_settlements: !!res.data.show_ytd_on_settlements,
        week_start_day: res.data.week_start_day ?? 6,
        feature_toggles: res.data.feature_toggles || {
          schedule: true, accounting: true, deductions: true, recurring_deductions: true,
          history: true, owner_operators: true, vehicle_management: true, driver_files: true,
        },
      });
      // Pre-fill the Contact form's company name + phone from the saved settings so the
      // support email always shows where the request is coming from. User can still edit.
      setContactForm((prev) => ({
        ...prev,
        company: prev.company || res.data.name || '',
        phone: prev.phone || res.data.phone || '',
        email: prev.email || res.data.email || '',
      }));
    } catch (error) {
      toast.error('Failed to load company info');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Combine address fields for legacy support
      const fullAddress = [
        formData.street_address,
        formData.city,
        formData.state,
        formData.zipcode
      ].filter(Boolean).join(', ');
      
      const data = {
        ...formData,
        address: fullAddress || null,
      };
      
      const res = await companyApi.update(data);
      setCompany(res.data);
      toast.success('Company information updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Image must be smaller than 100MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const res = await companyApi.uploadLogo(file);
      setCompany(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the company logo?')) return;
    
    try {
      await companyApi.deleteLogo();
      setCompany(prev => ({ ...prev, logo_url: null }));
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handleMigrateDispatchFees = async () => {
    if (!window.confirm('This will recalculate all dispatch fees for your loads to use the correct 10% formula. Continue?')) {
      return;
    }
    
    setMigratingFees(true);
    try {
      const res = await companyApi.migrateDispatchFees();
      toast.success(`Fixed ${res.data.updated_count} loads out of ${res.data.total_loads} total`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to migrate fees');
    } finally {
      setMigratingFees(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="company-settings-loading">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="company-settings-page">
      {/* Tour overlay (admin can re-trigger from Onboarding tab) */}
      {showTour && <OnboardingTour forceStart onClose={() => setShowTour(false)} />}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-manrope font-bold text-foreground">Company Settings</h1>
        <p className="text-muted-foreground mt-1">In-depth control over branding, features, roles, and onboarding</p>
      </div>

      <Tabs defaultValue="company" className="space-y-4">
        {/* Horizontal-scroll on small screens so each pill is large enough to tap;
            falls back to a 6-col grid on >=sm where there's room for all tabs. */}
        <TabsList className="flex w-full overflow-x-auto sm:grid sm:grid-cols-6 sm:max-w-4xl gap-1 h-auto p-1 justify-start sm:justify-stretch">
          <TabsTrigger value="company" data-testid="tab-company" className="flex-shrink-0 px-3 py-2"><Building2 className="h-3.5 w-3.5 mr-1.5" />Company</TabsTrigger>
          <TabsTrigger value="features" data-testid="tab-features" className="flex-shrink-0 px-3 py-2"><Sliders className="h-3.5 w-3.5 mr-1.5" />Features</TabsTrigger>
          <TabsTrigger value="permissions" data-testid="tab-permissions" className="flex-shrink-0 px-3 py-2"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Roles</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="flex-shrink-0 px-3 py-2"><UsersIcon className="h-3.5 w-3.5 mr-1.5" />Users</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="flex-shrink-0 px-3 py-2"><PlayCircle className="h-3.5 w-3.5 mr-1.5" />Tour</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact" className="flex-shrink-0 px-3 py-2"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />Contact</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ImageIcon className="h-5 w-5 text-blue-600" />
              Company Logo
            </CardTitle>
            <CardDescription>
              This logo will appear on the app and PDF exports
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo Preview */}
            <div className="flex justify-center">
              <div className="w-40 h-40 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden">
                {company?.logo_url ? (
                  <img 
                    src={company.logo_url} 
                    alt="Company Logo" 
                    className="w-full h-full object-contain p-2"
                    data-testid="company-logo-preview"
                  />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No logo uploaded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload/Delete Buttons */}
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {company?.logo_url ? 'Change' : 'Upload'}
              </Button>
              {company?.logo_url && (
                <Button
                  variant="outline"
                  onClick={handleDeleteLogo}
                  className="text-red-600 hover:text-red-700"
                  data-testid="delete-logo-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              PNG, JPG up to 100MB. Square images work best.
            </p>
          </CardContent>
        </Card>

        {/* Company Info Form */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
              Company Information
            </CardTitle>
            <CardDescription>
              This information will appear on shared documents and PDFs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your Company Name"
                  required
                  data-testid="company-name-input"
                />
              </div>

              <div className="space-y-4">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  Address
                </Label>
                <div className="space-y-2">
                  <Input
                    id="street_address"
                    value={formData.street_address}
                    onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                    placeholder="Street Address"
                    data-testid="company-street-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="City"
                      data-testid="company-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="State"
                      data-testid="company-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Input
                      id="zipcode"
                      value={formData.zipcode}
                      onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })}
                      placeholder="ZIP Code"
                      data-testid="company-zipcode-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="company-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@company.com"
                    data-testid="company-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  Website
                </Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="www.yourcompany.com"
                  data-testid="company-website-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mc_number" className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    MC Number
                  </Label>
                  <Input
                    id="mc_number"
                    value={formData.mc_number}
                    onChange={(e) => setFormData({ ...formData, mc_number: e.target.value })}
                    placeholder="MC-123456"
                    data-testid="company-mc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dot_number" className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    DOT Number
                  </Label>
                  <Input
                    id="dot_number"
                    value={formData.dot_number}
                    onChange={(e) => setFormData({ ...formData, dot_number: e.target.value })}
                    placeholder="DOT-123456"
                    data-testid="company-dot-input"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={saving}
                  data-testid="save-company-btn"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Save className="h-4 w-4" />
                      Save Changes
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">PDF Preview</CardTitle>
          <CardDescription>
            This is how your company info will appear on exported PDFs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-white border rounded-lg p-6 max-w-2xl">
            <div className="flex items-start gap-4">
              {company?.logo_url && (
                <img 
                  src={company.logo_url} 
                  alt="Logo" 
                  className="w-16 h-16 object-contain"
                />
              )}
              <div>
                <h3 className="text-xl font-bold text-blue-800">{formData.name || 'Your Company Name'}</h3>
                {formData.address && (
                  <p className="text-sm text-slate-600">{formData.address}</p>
                )}
                <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-500">
                  {formData.phone && <span>Phone: {formData.phone}</span>}
                  {formData.email && <span>Email: {formData.email}</span>}
                </div>
                <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-500">
                  {formData.mc_number && <span>MC# {formData.mc_number}</span>}
                  {formData.dot_number && <span>DOT# {formData.dot_number}</span>}
                </div>
              </div>
            </div>
            <div className="border-t mt-4 pt-4">
              <p className="text-slate-400 text-xs">Weekly Summary - Unit TRK-001</p>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* ── Features tab ── */}
        <TabsContent value="features" className="space-y-4">
          {/* AI Assistant + Default Broker + Week Start (instant-save preferences) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-manrope">
                <Sparkles className="h-5 w-5 text-blue-600" />Company Preferences
              </CardTitle>
              <CardDescription>
                These apply company-wide. Changes save instantly.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* AI Assistant — temporarily disabled site-wide (kept dormant for future reactivation). */}
              {false && (
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
                <div className="flex-1">
                  <Label htmlFor="ai_toggle_ft" className="font-semibold cursor-pointer flex items-center gap-2">
                    AI Assistant
                    <span className="text-[9px] px-1.5 py-px rounded bg-amber-100 text-amber-800 font-semibold uppercase">Off by default</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Shows a floating chat bubble (bottom-right) that answers questions about your fleet, drivers, and loads.
                  </p>
                </div>
                <Switch
                  id="ai_toggle_ft"
                  checked={formData.ai_assistant_enabled}
                  onCheckedChange={(checked) => {
                    const next = { ...formData, ai_assistant_enabled: checked };
                    setFormData(next);
                    companyApi.update(next)
                      .then(() => {
                        toast.success(`AI Assistant ${checked ? 'enabled' : 'disabled'}`);
                        window.dispatchEvent(new CustomEvent('ai-assistant-toggled'));
                      })
                      .catch(() => toast.error('Failed to save'));
                  }}
                  data-testid="ai-toggle"
                />
              </div>
              )}

              {/* Default Broker */}
              <div className="p-3 rounded-lg border space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label htmlFor="default_broker_toggle" className="font-semibold cursor-pointer">Default Broker on New Loads</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      When ON, the Add Load form pre-fills this broker (e.g. Amazon) and lets users flip to a custom broker. When OFF, no default broker is offered — every new load starts on the custom broker picker.
                    </p>
                  </div>
                  <Switch
                    id="default_broker_toggle"
                    checked={formData.use_default_broker}
                    onCheckedChange={(checked) => {
                      const next = { ...formData, use_default_broker: checked };
                      setFormData(next);
                      companyApi.update(next).then(() => toast.success(`Default broker ${checked ? 'enabled' : 'disabled'}`)).catch(() => {});
                    }}
                    data-testid="default-broker-toggle"
                  />
                </div>
                {formData.use_default_broker && (
                  <div className="space-y-2">
                    <Input
                      value={formData.default_broker}
                      onChange={(e) => setFormData({ ...formData, default_broker: e.target.value })}
                      onBlur={() => companyApi.update(formData).catch(() => {})}
                      placeholder="Amazon"
                      data-testid="default-broker-input"
                    />
                    <div className="flex gap-1 flex-wrap">
                      {['Amazon', 'CH Robinson', 'JB Hunt', 'Convoy', 'Uber Freight'].map((b) => (
                        <Button key={b} size="sm" variant="outline" onClick={() => {
                          const next = { ...formData, default_broker: b };
                          setFormData(next);
                          companyApi.update(next).then(() => toast.success(`Default broker set to ${b}`)).catch(() => {});
                        }}>{b}</Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Show Year-to-Date Section on Settlements */}
              <div className="p-3 rounded-lg border space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label htmlFor="show_ytd_toggle" className="font-semibold cursor-pointer">Show Year-to-Date Section on Settlements</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      When ON, every PDF settlement (Dashboard, Weekly Summary, Owner Operators) and the on-screen summary cards display a Year-to-Date roll-up (gross, fees, deductions, net) at the bottom. When OFF (default), that section is hidden everywhere and only the period you're viewing is shown.
                    </p>
                  </div>
                  <Switch
                    id="show_ytd_toggle"
                    checked={formData.show_ytd_on_settlements}
                    onCheckedChange={(checked) => {
                      const next = { ...formData, show_ytd_on_settlements: checked };
                      setFormData(next);
                      companyApi.update(next).then(() => toast.success(`Year-to-Date section ${checked ? 'enabled' : 'hidden'}`)).catch(() => {});
                    }}
                    data-testid="show-ytd-toggle"
                  />
                </div>
              </div>

              {/* Week Start Day */}
              <div className="p-3 rounded-lg border space-y-2">
                <Label htmlFor="week_start_select" className="font-semibold">Week Start Day</Label>
                <p className="text-xs text-muted-foreground">
                  Used across Weekly Loads, Summary, Schedule, and Paystubs. Applies to new loads going forward — old loads keep their original week.
                </p>
                <Select
                  value={String(formData.week_start_day)}
                  onValueChange={(v) => {
                    const day = parseInt(v, 10);
                    const next = { ...formData, week_start_day: day };
                    setFormData(next);
                    companyApi.update(next).then(() => toast.success('Week start day updated')).catch(() => {});
                  }}
                >
                  <SelectTrigger id="week_start_select" data-testid="week-start-day-select" className="max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">Sunday → Saturday (recommended)</SelectItem>
                    <SelectItem value="0">Monday → Sunday</SelectItem>
                    <SelectItem value="1">Tuesday → Monday</SelectItem>
                    <SelectItem value="2">Wednesday → Tuesday</SelectItem>
                    <SelectItem value="3">Thursday → Wednesday</SelectItem>
                    <SelectItem value="4">Friday → Thursday</SelectItem>
                    <SelectItem value="5">Saturday → Friday</SelectItem>
                  </SelectContent>
                </Select>
                {formData.week_start_day !== 6 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-blue-600 hover:text-blue-700 mt-1 h-7"
                    onClick={() => {
                      const next = { ...formData, week_start_day: 6 };
                      setFormData(next);
                      companyApi.update(next).then(() => toast.success('Reverted to Sunday')).catch(() => {});
                    }}
                    data-testid="revert-week-start-btn"
                  >
                    Revert to default (Sunday)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-manrope">
                <Sliders className="h-5 w-5 text-blue-600" />Feature Visibility
              </CardTitle>
              <CardDescription>
                Turn entire modules on or off for the whole company. Disabled modules disappear from the sidebar for everyone.
                Changes save instantly.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const features = [
                  { key: 'schedule', label: 'Driver Schedule', icon: '📅', desc: 'Drag-and-drop weekly Gantt chart for assigning drivers to contracts.' },
                  { key: 'accounting', label: 'Accounting', icon: '💼', defaultOff: true, desc: 'Bank statement parsing, P&L, transactions ledger.' },
                  { key: 'expenses', label: 'Expenses', icon: '💳', defaultOff: true, desc: 'Track non-driver business expenses (rent, insurance, utilities).' },
                  { key: 'deductions', label: 'Deductions', icon: '➖', desc: 'Per-load and per-week driver deductions.' },
                  { key: 'recurring_deductions', label: 'Recurring Deductions', icon: '🔁', desc: 'Auto-applied weekly deductions (truck lease, ELD, insurance).' },
                  { key: 'history', label: 'History', icon: '🕘', desc: 'Audit log of all settlements and changes.' },
                  { key: 'owner_operators', label: 'Owner Operators', icon: '👤', desc: 'Contracted truck owners with their own settlement workflow.' },
                  { key: 'vehicle_management', label: 'Vehicle Mgmt', icon: '🚚', desc: 'Vehicle docs, maintenance, accident registry.' },
                  { key: 'driver_files', label: 'Driver Files', icon: '📁', desc: 'CDL, medical card, contracts uploads per driver.' },
                  { key: 'bulk_email_settlements', label: 'Bulk Email Settlements', icon: '📤', defaultOff: true, desc: 'Show the "Email All Settlements" button on Summary page. Off by default to prevent accidents.' },
                ];
                const activeCount = features.filter(f => (f.defaultOff ? formData.feature_toggles?.[f.key] === true : formData.feature_toggles?.[f.key] !== false)).length;
                return (
                  <>
                    <div className="mb-4 flex items-center justify-between text-xs text-muted-foreground">
                      <span><strong className="text-foreground">{activeCount}</strong> of {features.length} features enabled</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {features.map(f => {
                        const isOn = f.defaultOff
                          ? formData.feature_toggles?.[f.key] === true
                          : formData.feature_toggles?.[f.key] !== false;
                        return (
                          <div
                            key={f.key}
                            className={`relative rounded-xl border p-4 transition-all ${isOn ? 'bg-white dark:bg-slate-900 border-blue-200 dark:border-blue-500/40 shadow-sm' : 'bg-muted/30 border-slate-200 dark:border-slate-800'}`}
                            data-testid={`feature-card-${f.key}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-lg" aria-hidden>{f.icon}</span>
                                  <Label htmlFor={`tab_ft_${f.key}`} className={`font-semibold cursor-pointer ${!isOn ? 'text-muted-foreground' : ''}`}>{f.label}</Label>
                                  {f.defaultOff && (
                                    <span className="text-[9px] px-1.5 py-px rounded bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 font-semibold uppercase">Off by default</span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                              </div>
                              <Switch
                                id={`tab_ft_${f.key}`}
                                checked={isOn}
                                onCheckedChange={(checked) => {
                                  const next = { ...formData.feature_toggles, [f.key]: checked };
                                  setFormData({ ...formData, feature_toggles: next });
                                  import('../lib/api').then(({ featureApi }) => {
                                    featureApi.updateToggles(next)
                                      .then(() => toast.success(`${f.label} ${checked ? 'enabled' : 'disabled'}`))
                                      .catch(() => toast.error('Failed to update'));
                                  });
                                }}
                                data-testid={`features-toggle-${f.key}`}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Scheduled Settlement Emails — mounted under Features so admins can
              manage automated PDF delivery from a single settings hub. */}
          <SettlementScheduleCard />
        </TabsContent>

        {/* ── Roles & Permissions tab ── */}
        <TabsContent value="permissions" className="space-y-4">
          <RolesPermissionsTab />
        </TabsContent>

        {/* ── User Management tab ── */}
        <TabsContent value="users" className="space-y-4">
          <UserManagement embedded />
        </TabsContent>

        {/* ── Onboarding tab ── */}
        <TabsContent value="onboarding" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-manrope">
                <PlayCircle className="h-5 w-5 text-blue-600" />Onboarding Tour
              </CardTitle>
              <CardDescription>
                A short interactive walkthrough that highlights each main feature in the sidebar.
                It runs automatically the first time a new user signs in. Re-launch it any time below.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-muted/30 p-4 text-sm space-y-1.5">
                <p><strong>What it covers:</strong></p>
                <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                  <li>Dashboard at-a-glance metrics</li>
                  <li>Weekly Loads & Amazon Relay import</li>
                  <li>People menu (Drivers, Staff, Owner-Operators)</li>
                  <li>Schedule drag-and-drop Gantt</li>
                  <li>Units, Driver Files, Company Settings</li>
                </ul>
              </div>
              <Button onClick={() => setShowTour(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="restart-tour-btn">
                <PlayCircle className="h-4 w-4 mr-2" />Restart Tour
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== Contact Tab ============== */}
        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />Contact My Dispatch Support
              </CardTitle>
              <CardDescription>
                Send us your question, feature request, or issue and we'll reply to your email.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Polite onboarding nudge */}
              <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 flex items-start gap-3">
                <PlayCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900 mb-1">First time here? Take the tour</p>
                  <p className="text-xs text-blue-800/80">
                    Many questions are answered by our quick interactive onboarding tour — it covers loads, drivers, paystubs, settlements, scheduling, and more in under 3 minutes. Feel free to take it before reaching out.
                  </p>
                  <Button
                    onClick={() => setShowTour(true)}
                    variant="outline"
                    size="sm"
                    className="mt-3 border-blue-300 text-blue-700 hover:bg-blue-100"
                    data-testid="contact-start-tour"
                  >
                    <PlayCircle className="h-3.5 w-3.5 mr-1.5" />Start the Tour
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Your Name</Label>
                  <Input
                    id="contact-name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Reply Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    placeholder="you@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-company">Company</Label>
                  <Input
                    id="contact-company"
                    value={contactForm.company}
                    onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                    placeholder="Your trucking company"
                    data-testid="contact-company-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Phone (optional)</Label>
                  <Input
                    id="contact-phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-subject">Subject</Label>
                <Input
                  id="contact-subject"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  placeholder="e.g. Feature request, Question, Bug report"
                  data-testid="contact-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="What can we help with?"
                  rows={7}
                  data-testid="contact-message-input"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={async () => {
                    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
                      toast.error('All fields are required');
                      return;
                    }
                    setSendingContact(true);
                    try {
                      await contactApi.send(contactForm);
                      toast.success('Message sent — we\'ll reply soon!');
                      setContactForm({ name: '', email: '', company: formData.name || '', phone: formData.phone || '', subject: '', message: '' });
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to send. Please try again.');
                    } finally {
                      setSendingContact(false);
                    }
                  }}
                  disabled={sendingContact}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="contact-send-btn"
                >
                  {sendingContact ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Message
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
